import { config } from '../config';
import { ApiResponse, ExperimentDailyModule } from '../types';

class ApiService {
  private baseURL: string;
  private defaultHeaders: HeadersInit;

  constructor() {
    this.baseURL = config.API_BASE_URL;
    this.defaultHeaders = config.DEFAULT_HEADERS;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('authToken');

    // For FormData, don't include default headers that might set Content-Type
    const isFormData = options.body instanceof FormData;
    const headers: HeadersInit = isFormData
      ? { ...options.headers }
      : { ...this.defaultHeaders, ...options.headers };

    // Add dynamic Origin and Referer headers based on current location
    if (typeof window !== 'undefined') {
      (headers as Record<string, string>)['Origin'] = window.location.origin;
      (headers as Record<string, string>)['Referer'] = window.location.href;
    }

    if (token) {
      (headers as Record<string, string>)['x-auth-token'] = token;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      console.log('API Response:', { status: response.status, data });

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP error! status: ${response.status}`
        );
      }

      return {
        data,
      };
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'GET',
    });
  }

  async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async postFormData<T>(
    endpoint: string,
    formData: FormData
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
      // Do NOT set Content-Type; browser will set it with boundary
    });
  }

  async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  async patch<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async postText(endpoint: string, data: any): Promise<string> {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      accept: 'text/plain',
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['x-auth-token'] = token;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return response.text();
  }

  async createExperimentDailyModule(
    data: ExperimentDailyModule
  ): Promise<ApiResponse> {
    return this.post('/experiment-daily-modules/', data);
  }

  async getExperimentDailyModules(): Promise<
    ApiResponse<ExperimentDailyModule[]>
  > {
    return this.get('/experiment-daily-modules/');
  }

  async updateExperimentDailyModule(
    id: string,
    data: ExperimentDailyModule
  ): Promise<ApiResponse> {
    return this.put(`/experiment-daily-modules/${id}`, data);
  }
}

export default new ApiService();
